import { useState, useEffect, useCallback, Fragment } from 'react';
import './TeamsPage.css';

const API_URL = (() => {
    if (typeof window === 'undefined') return '';

    const host = window.location.hostname;
    const protocol = window.location.protocol;

    if (host === 'localhost') {
        return `http://localhost:4000`; // local dev backend
    }

    return `${protocol}//${host}`;
})();

const STATUS_LABELS = {
    not_started: 'Nie rozpoczęła',
    in_progress: 'W trakcie gry',
    finished: 'Zakończyła grę'
};

const TABS = [
    { key: 'in_progress', label: 'W trakcie' },
    { key: 'finished', label: 'Zakończone' },
    { key: 'not_started', label: 'Nierozpoczęte' },
    { key: 'all', label: 'Wszystkie' }
];

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
};

const REFRESH_INTERVAL_MS = 15000;

const HIDDEN_PHONE_NUMBERS = ['+48794432288', '+48886354785'];

const TeamsPage = () => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedToken, setExpandedToken] = useState(null);
    const [activeTab, setActiveTab] = useState('in_progress');
    const [deletingToken, setDeletingToken] = useState(null);
    const [teamToDelete, setTeamToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [feedbackTeam, setFeedbackTeam] = useState(null);
    const [emailEvents, setEmailEvents] = useState([]);
    const [resolvingEventId, setResolvingEventId] = useState(null);
    const [hideTestTeams, setHideTestTeams] = useState(true);
    const [questInfoTeam, setQuestInfoTeam] = useState(null);

    const fetchTeams = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/teams`);
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Błąd pobierania drużyn');
            setTeams(Array.isArray(data) ? data : []);
            setError(null);
        } catch (err) {
            console.error('Błąd pobierania listy drużyn:', err);
            setError('Nie udało się pobrać listy drużyn.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchEmailEvents = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/email-events`);
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Błąd pobierania zdarzeń email');
            setEmailEvents(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Błąd pobierania zdarzeń email:', err);
        }
    }, []);

    useEffect(() => {
        fetchTeams();
        fetchEmailEvents();
        const interval = setInterval(() => {
            fetchTeams();
            fetchEmailEvents();
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchTeams, fetchEmailEvents]);

    const resolveEmailEvent = async (id) => {
        setResolvingEventId(id);
        try {
            const response = await fetch(`${API_URL}/api/email-events/${id}/resolve`, { method: 'PATCH' });
            if (!response.ok) throw new Error('Błąd aktualizacji zdarzenia');
            setEmailEvents(prev => prev.filter(ev => ev._id !== id));
        } catch (err) {
            console.error('Błąd oznaczania zdarzenia email jako przejrzane:', err);
        } finally {
            setResolvingEventId(null);
        }
    };

    const toggleExpand = (token) => {
        setExpandedToken(prev => (prev === token ? null : token));
    };

    const handleDeleteTeam = (team) => {
        setDeleteError(null);
        setTeamToDelete(team);
    };

    const cancelDeleteTeam = () => {
        if (deletingToken) return;
        setTeamToDelete(null);
        setDeleteError(null);
    };

    const confirmDeleteTeam = async () => {
        if (!teamToDelete) return;
        setDeletingToken(teamToDelete.token);
        try {
            const response = await fetch(`${API_URL}/api/teams/${teamToDelete.token}`, { method: 'DELETE' });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Błąd usuwania drużyny');
            setTeams(prev => prev.filter(t => t.token !== teamToDelete.token));
            setTeamToDelete(null);
        } catch (err) {
            console.error('Błąd usuwania drużyny:', err);
            setDeleteError('Nie udało się usunąć drużyny.');
        } finally {
            setDeletingToken(null);
        }
    };

    const filteredTeams = hideTestTeams
        ? teams.filter(team => !HIDDEN_PHONE_NUMBERS.includes(team.phoneNumber))
        : teams;

    const counts = filteredTeams.reduce((acc, team) => {
        acc[team.status] = (acc[team.status] || 0) + 1;
        return acc;
    }, {});

    const visibleTeams = activeTab === 'all' ? filteredTeams : filteredTeams.filter(team => team.status === activeTab);

    if (activeTab === 'not_started') {
        visibleTeams.sort((a, b) => {
            if (!a.createdAt && !b.createdAt) return 0;
            if (!a.createdAt) return 1;
            if (!b.createdAt) return -1;
            return new Date(b.createdAt) - new Date(a.createdAt);
        });
    } else {
        visibleTeams.sort((a, b) => new Date(b.gameStartedAt || b.createdAt || 0) - new Date(a.gameStartedAt || a.createdAt || 0));
    }

    return (
        <div className="teams-page">
            <h1>Lista drużyn</h1>

            <div className="teams-page__toolbar">
                <button
                    type="button"
                    className={`teams-page__toggle-test-btn ${hideTestTeams ? '' : '-active'}`}
                    onClick={() => setHideTestTeams(prev => !prev)}
                >
                    {hideTestTeams ? 'Pokaż testowe' : 'Ukryj testowe'}
                </button>
            </div>

            {emailEvents.length > 0 && (
                <div className="teams-page__email-alerts">
                    {emailEvents.map(ev => (
                        <div key={ev._id} className="teams-page__email-alert">
                            <span className="teams-page__email-alert-icon">⚠️</span>
                            <span className="teams-page__email-alert-text">
                                Drużyna <strong>{ev.to || '—'}</strong> mogła nie otrzymać emaila z dostępem do gry
                                {ev.reason ? ` (${ev.reason})` : ''}.
                            </span>
                            <button
                                type="button"
                                className="teams-page__email-alert-close"
                                onClick={() => resolveEmailEvent(ev._id)}
                                disabled={resolvingEventId === ev._id}
                                title="Oznacz jako przejrzane"
                            >
                                {resolvingEventId === ev._id ? '...' : '✕'}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="teams-page__tabs">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`teams-page__tab ${activeTab === tab.key ? '-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                        <span className="teams-page__tab-count">
                            {tab.key === 'all' ? filteredTeams.length : (counts[tab.key] || 0)}
                        </span>
                    </button>
                ))}
            </div>

            {loading && <p className="teams-page__info">Ładowanie...</p>}
            {error && <p className="teams-page__error">{error}</p>}

            {!loading && !error && (
                visibleTeams.length === 0 ? (
                    <p className="teams-page__info">Brak drużyn w tej kategorii.</p>
                ) : (
                    <div className="teams-page__table-wrap">
                        <table className="teams-page__table">
                            <thead>
                                <tr>
                                    <th>Drużyna</th>
                                    <th>Token</th>
                                    <th>Status</th>
                                    <th>Aktualna zagadka</th>
                                    <th>Punkty</th>
                                    <th>Start gry</th>
                                    <th>Czas gry</th>
                                    <th>Podpowiedzi</th>
                                    <th>Pominięcia</th>
                                    <th>Ostatnia aktywność</th>
                                    <th>Email</th>
                                    <th>Telefon</th>
                                    <th>Feedback</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleTeams.map(team => (
                                    <Fragment key={team.token}>
                                        <tr className={`teams-page__row -${team.status}`}>
                                            <td>{team.name || <em>(bez nazwy)</em>}</td>
                                            <td className="teams-page__token">{team.token}</td>
                                            <td>
                                                <span className={`teams-page__status-badge -${team.status}`}>
                                                    {STATUS_LABELS[team.status] || team.status}
                                                </span>
                                            </td>
                                            <td>
                                                {team.status === 'finished'
                                                    ? 'Koniec gry'
                                                    : (team.currentQuestIndex
                                                        ? (
                                                            <span className="teams-page__quest-cell">
                                                                {`#${team.currentQuestIndex} (${team.currentQuestId})`}
                                                                {team.currentQuestDescription && (
                                                                    <button
                                                                        type="button"
                                                                        className="teams-page__quest-info-btn"
                                                                        onClick={() => setQuestInfoTeam(team)}
                                                                        title="Zobacz opis zagadki"
                                                                    >
                                                                        i
                                                                    </button>
                                                                )}
                                                            </span>
                                                        )
                                                        : '—')}
                                            </td>
                                            <td>{team.points}</td>
                                            <td>{formatDate(team.gameStartedAt)}</td>
                                            <td>{team.gameDuration || '—'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="teams-page__hints-btn"
                                                    onClick={() => toggleExpand(team.token)}
                                                    disabled={!team.hintsUsed?.length}
                                                >
                                                    {team.hintsUsed?.length || 0} {expandedToken === team.token ? '▲' : '▼'}
                                                </button>
                                            </td>
                                            <td className="-nowrap">{team.skipNextQuestCount || 0}</td>
                                            <td className="-nowrap">{formatDate(team.lastSeenAt)}</td>
                                            <td>{team.email || '—'}</td>
                                            <td>{team.phoneNumber || '—'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`teams-page__feedback-btn ${team.feedback?.length ? '-has-feedback' : ''}`}
                                                    onClick={() => setFeedbackTeam(team)}
                                                    disabled={!team.feedback?.length}
                                                    title={team.feedback?.length ? 'Zobacz feedback' : 'Brak feedbacku'}
                                                >
                                                    {team.feedback?.length ? '✓' : '—'}
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="teams-page__delete-btn"
                                                    onClick={() => handleDeleteTeam(team)}
                                                    disabled={deletingToken === team.token}
                                                    title="Usuń drużynę"
                                                >
                                                    {deletingToken === team.token ? '...' : '🗑'}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedToken === team.token && team.hintsUsed?.length > 0 && (
                                            <tr className="teams-page__hints-row">
                                                <td colSpan={13}>
                                                    <ul className="teams-page__hints-list">
                                                        {team.hintsUsed.map((hint, i) => (
                                                            <li key={i}>
                                                                <strong>
                                                                    Zagadka {hint.quest_index ? `#${hint.quest_index}` : ''} ({hint.quest_id})
                                                                </strong>
                                                                , podpowiedź {hint.hint_id}: {hint.text}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {questInfoTeam && (
                <div className="teams-page__modal-overlay" onClick={() => setQuestInfoTeam(null)}>
                    <div className="teams-page__modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Zagadka #{questInfoTeam.currentQuestIndex} ({questInfoTeam.currentQuestId})</h2>
                        <p>{questInfoTeam.currentQuestDescription}</p>
                        <div className="teams-page__modal-actions">
                            <button
                                type="button"
                                className="teams-page__modal-btn -cancel"
                                onClick={() => setQuestInfoTeam(null)}
                            >
                                Zamknij
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {teamToDelete && (
                <div className="teams-page__modal-overlay" onClick={cancelDeleteTeam}>
                    <div className="teams-page__modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Usunąć drużynę?</h2>
                        <p>
                            Czy na pewno chcesz usunąć drużynę{' '}
                            <strong>{teamToDelete.name || teamToDelete.token}</strong>
                            {teamToDelete.name ? ` (${teamToDelete.token})` : ''} z bazy?
                            Tej operacji nie można cofnąć.
                        </p>
                        {deleteError && <p className="teams-page__modal-error">{deleteError}</p>}
                        <div className="teams-page__modal-actions">
                            <button
                                type="button"
                                className="teams-page__modal-btn -cancel"
                                onClick={cancelDeleteTeam}
                                disabled={!!deletingToken}
                            >
                                Anuluj
                            </button>
                            <button
                                type="button"
                                className="teams-page__modal-btn -confirm"
                                onClick={confirmDeleteTeam}
                                disabled={!!deletingToken}
                            >
                                {deletingToken ? 'Usuwanie...' : 'Usuń'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {feedbackTeam && (
                <div className="teams-page__modal-overlay" onClick={() => setFeedbackTeam(null)}>
                    <div className="teams-page__modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Feedback drużyny {feedbackTeam.name || feedbackTeam.token}</h2>
                        <div className="teams-page__feedback-list">
                            {(feedbackTeam.feedback || []).map((fb, i) => (
                                <div key={i} className="teams-page__feedback-entry">
                                    {feedbackTeam.feedback.length > 1 && (
                                        <div className="teams-page__feedback-date">{formatDate(fb.submittedAt)}</div>
                                    )}
                                    {typeof fb.rating === 'number' && (
                                        <p><strong>Ocena:</strong> {fb.rating}/5</p>
                                    )}
                                    {fb.liked && (
                                        <p><strong>Co się podobało:</strong> {fb.liked}</p>
                                    )}
                                    {fb.improvement && (
                                        <p><strong>Co poprawić:</strong> {fb.improvement}</p>
                                    )}
                                    {fb.otherFeedback && (
                                        <p><strong>Dodatkowa opinia:</strong> {fb.otherFeedback}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="teams-page__modal-actions">
                            <button
                                type="button"
                                className="teams-page__modal-btn -cancel"
                                onClick={() => setFeedbackTeam(null)}
                            >
                                Zamknij
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamsPage;

